import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles, Clock, DollarSign, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resolveUrl = (url) => {
  if (!url) return "";
  if (url.includes("/api/files/") && !url.startsWith(process.env.REACT_APP_BACKEND_URL)) {
    return `${process.env.REACT_APP_BACKEND_URL}${url.substring(url.indexOf("/api/files/"))}`;
  }
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const questions = [
  { id: "hair_length", title: "What's your hair length?", options: ["Short (ear-chin)", "Medium (shoulder)", "Long (mid-back+)"] },
  { id: "occasion", title: "What's the occasion?", options: ["Daily / Everyday", "Work / Professional", "Wedding / Special Event", "Vacation / Travel", "Just want a change"] },
  { id: "time_available", title: "How much time do you have?", options: ["Under 2 hours", "2-3 hours", "3-4 hours", "I have all day"] },
  { id: "budget", title: "What's your budget?", options: ["Under $100", "$100-$200", "Over $200", "No limit"] },
];

const StyleFinderPage = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAnswer = (value) => {
    const q = questions[step];
    const newAnswers = { ...answers, [q.id]: value };
    setAnswers(newAnswers);

    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      submitQuiz(newAnswers);
    }
  };

  const submitQuiz = async (finalAnswers) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/style-finder`, finalAnswers);
      setResults(res.data);
    } catch {
      toast.error("Couldn't load recommendations. Try again!");
    } finally {
      setLoading(false);
    }
  };

  const scrollToBooking = () => {
    window.location.href = "/#booking";
  };

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col" data-testid="style-finder-page">
      <header className="border-b border-brand-border bg-brand-surface px-6 py-4 flex items-center justify-between">
        <Link to="/" className="font-serif text-xl text-brand-text">Afro <span className="text-brand-gold">Magic</span></Link>
        <Link to="/" className="flex items-center gap-1 font-sans text-xs text-brand-text-muted hover:text-brand-gold"><ArrowLeft size={14} /> Back</Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        {loading ? (
          <div className="text-center">
            <Loader2 size={40} className="text-brand-gold mx-auto mb-4 animate-spin" />
            <p className="font-serif text-xl text-brand-text">Finding your perfect styles...</p>
            <p className="font-sans text-sm text-brand-text-muted mt-2">Our AI is matching you with the best braids</p>
          </div>
        ) : results ? (
          <div className="w-full max-w-3xl" data-testid="style-finder-results">
            <div className="text-center mb-8">
              <Sparkles size={32} className="text-brand-gold mx-auto mb-3" />
              <h2 className="font-serif text-3xl text-brand-text">Your Perfect Styles</h2>
              <p className="font-sans text-sm text-brand-text-muted mt-2">Based on your preferences, we recommend:</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {results.map((svc, i) => (
                <motion.div
                  key={svc.id || i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15 }}
                  className="border border-brand-border bg-brand-surface overflow-hidden"
                  data-testid={`style-result-${i}`}
                >
                  {i === 0 && <div className="bg-brand-gold text-black text-center py-1 font-sans text-[10px] font-bold uppercase tracking-wider">Best Match</div>}
                  {svc.image_url && <img src={resolveUrl(svc.image_url)} alt={svc.name} className="w-full h-40 object-cover" />}
                  <div className="p-4">
                    <h3 className="font-serif text-lg text-brand-text mb-1">{svc.name}</h3>
                    <p className="font-sans text-xs text-brand-gold mb-2 italic">{svc.reason}</p>
                    <div className="flex items-center justify-between mb-3">
                      <span className="flex items-center gap-1 font-sans text-xs text-brand-text-muted"><Clock size={12} className="text-brand-gold" /> {svc.duration}</span>
                      <span className="flex items-center gap-1 font-sans text-sm text-brand-gold font-semibold"><DollarSign size={12} /> {svc.starting_price}</span>
                    </div>
                    <button onClick={scrollToBooking} className="w-full py-2 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light transition-colors" data-testid={`book-result-${i}`}>
                      Book Now
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-6">
              <button onClick={() => { setResults(null); setStep(0); setAnswers({}); }} className="font-sans text-xs text-brand-gold underline hover:text-brand-gold-light">Retake Quiz</button>
            </div>
          </div>
        ) : (
          <div className="w-full max-w-md" data-testid="style-finder-quiz">
            <div className="text-center mb-8">
              <Sparkles size={28} className="text-brand-gold mx-auto mb-3" />
              <h1 className="font-serif text-3xl text-brand-text">Braid Style Finder</h1>
              <p className="font-sans text-sm text-brand-text-muted mt-2">Answer 4 quick questions and we'll find your perfect style</p>
            </div>

            {/* Progress */}
            <div className="flex gap-1 mb-6">
              {questions.map((_, i) => (
                <div key={i} className={`flex-1 h-1 ${i <= step ? "bg-brand-gold" : "bg-brand-border"} transition-colors`} />
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.2 }}
              >
                <p className="font-sans text-xs text-brand-gold mb-2 uppercase tracking-wider">Question {step + 1} of {questions.length}</p>
                <h2 className="font-serif text-2xl text-brand-text mb-6">{questions[step].title}</h2>
                <div className="space-y-2">
                  {questions[step].options.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => handleAnswer(opt)}
                      data-testid={`quiz-option-${opt.replace(/\s/g, "-").toLowerCase()}`}
                      className="w-full text-left px-5 py-3.5 border border-brand-border text-brand-text font-sans text-sm hover:border-brand-gold hover:text-brand-gold transition-colors flex items-center justify-between group"
                    >
                      {opt}
                      <ArrowRight size={14} className="text-brand-text-muted group-hover:text-brand-gold transition-colors" />
                    </button>
                  ))}
                </div>
                {step > 0 && (
                  <button onClick={() => setStep(step - 1)} className="mt-4 font-sans text-xs text-brand-text-muted hover:text-brand-gold"><ArrowLeft size={12} className="inline mr-1" />Back</button>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default StyleFinderPage;
